import React, { useEffect, useState } from "react";
import AdminWrapper from "../../../components/AdminWrapper";
import { useDispatch, useSelector } from "react-redux";
import { GetAdmin } from "../../../redux/reducer/GetAdminDetail";
import {
  Box,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  IconButton,
  Button,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  FormControl,
  InputLabel,
  Select,
  Skeleton,
  Card,
  CardContent,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Search,
  Visibility,
  Person,
  Home,
  CheckCircle,
  Cancel,
  Close,
  Download,
  Refresh,
} from "@mui/icons-material";

const UserManagement = () => {
  const dispatch = useDispatch();
  const open = useSelector((state) => state.sidebar.open);
  const { loading, GetAdminDetail } = useSelector((state) => state.getAdminDetail);
  const stats = GetAdminDetail;

  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedUser, setSelectedUser] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    dispatch(GetAdmin());
  }, [dispatch]);

  // Combine landlords and tenants
  const allUsers = [
    ...(stats?.landlord_detail?.map((user) => ({
      ...user,
      userType: "landlord",
      contact: user.contactNumber,
      profileImage: user.profilePhoto,
      address: user.currentAddress,
      createdAt: user.date,
    })) || []),
    ...(stats?.tenant_detail?.map((user) => ({
      ...user,
      userType: "tenant",
      contact: user.mobileNumber,
      profileImage: user.profilePhoto,
      address: user.currentAddress,
      createdAt: user.date,
    })) || []),
  ];

  // Filter users based on search and filters
  const filteredUsers = allUsers.filter((user) => {
    const matchesSearch =
      (user.name?.toLowerCase() || "").includes(searchQuery.toLowerCase()) ||
      (user.email?.toLowerCase() || "").includes(searchQuery.toLowerCase()) ||
      (user.contact?.toLowerCase() || "").includes(searchQuery.toLowerCase());

    const matchesType =
      filterType === "all" || user.userType === filterType;

    const matchesStatus =
      filterStatus === "all" ||
      (filterStatus === "active" && user.is_active) ||
      (filterStatus === "inactive" && !user.is_active);

    return matchesSearch && matchesType && matchesStatus;
  });

  const handleViewDetails = (user) => {
    setSelectedUser(user);
    setDetailsOpen(true);
  };

  const handleRefresh = () => {
    dispatch(GetAdmin());
  };

  // Loading Skeleton
  const LoadingSkeleton = () => (
    <>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box>
          <Skeleton variant="text" width={200} height={40} />
          <Skeleton variant="text" width={180} height={20} />
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Skeleton variant="rounded" width={100} height={36} />
          <Skeleton variant="rounded" width={100} height={36} />
        </Box>
      </Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[1, 2, 3, 4].map((i) => (
          <Grid item xs={6} sm={3} key={i}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <Skeleton variant="text" width={60} height={45} sx={{ mx: "auto" }} />
                <Skeleton variant="text" width={80} height={20} sx={{ mx: "auto" }} />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      <Skeleton variant="rounded" width="100%" height={56} sx={{ mb: 2, borderRadius: 2 }} />
      <Skeleton variant="rounded" width="100%" height={400} sx={{ borderRadius: 2 }} />
    </>
  );

  const columns = [
    {
      field: "index",
      headerName: "#",
      width: 60,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
    },
    {
      field: "name",
      headerName: "User",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1 }}>
          <Avatar
            src={params.row.profileImage}
            sx={{
              width: 40,
              height: 40,
              bgcolor: params.row.userType === "landlord" ? "#22c55e20" : "#f59e0b20",
              color: params.row.userType === "landlord" ? "#22c55e" : "#f59e0b",
            }}
          >
            {(params.row.name || "U").charAt(0).toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {params.row.name || "N/A"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.email}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: "contact",
      headerName: "Contact",
      width: 140,
      renderCell: (params) => params.value || "N/A",
    },
    {
      field: "userType",
      headerName: "Type",
      width: 120,
      renderCell: (params) => (
        <Chip
          icon={params.value === "landlord" ? <Person fontSize="small" /> : <Home fontSize="small" />}
          label={params.value === "landlord" ? "Landlord" : "Tenant"}
          size="small"
          sx={{
            bgcolor: params.value === "landlord" ? "#22c55e15" : "#f59e0b15",
            color: params.value === "landlord" ? "#22c55e" : "#f59e0b",
            fontWeight: 500,
            "& .MuiChip-icon": {
              color: "inherit",
            },
          }}
        />
      ),
    },
    {
      field: "is_active",
      headerName: "Status",
      width: 120,
      renderCell: (params) => (
        <Chip
          icon={params.value ? <CheckCircle fontSize="small" /> : <Cancel fontSize="small" />}
          label={params.value ? "Active" : "Inactive"}
          size="small"
          sx={{
            bgcolor: params.value ? "#dcfce7" : "#fee2e2",
            color: params.value ? "#166534" : "#991b1b",
            fontWeight: 500,
            "& .MuiChip-icon": {
              color: "inherit",
            },
          }}
        />
      ),
    },
    {
      field: "createdAt",
      headerName: "Joined",
      width: 120,
      renderCell: (params) =>
        params.value ? new Date(params.value).toLocaleDateString() : "N/A",
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <IconButton size="small" onClick={() => handleViewDetails(params.row)}>
          <Visibility />
        </IconButton>
      ),
    },
  ];

  return (
    <>
      <AdminWrapper />
      <Box
        sx={{
          ml: { xs: 0, md: open ? "260px" : "65px" },
          transition: "margin 0.3s ease",
          minHeight: "100vh",
          bgcolor: "#f8fafc",
          p: { xs: 2, md: 3 },
          pt: { xs: 10, md: 11 },
        }}
      >
        {loading ? <LoadingSkeleton /> : (
        <>
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: "#1e293b" }}>
              User Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              View and manage all users on the platform
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={handleRefresh}
              sx={{ borderColor: "#e5e7eb", color: "#64748b" }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<Download />}
              sx={{ bgcolor: "#e94560", "&:hover": { bgcolor: "#d13652" } }}
            >
              Export
            </Button>
          </Box>
        </Box>

        {/* Stats Summary */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2.5, borderRadius: 2, textAlign: "center" }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: "#6366f1" }}>
                {allUsers.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Users
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2.5, borderRadius: 2, textAlign: "center" }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: "#22c55e" }}>
                {stats?.landlord_count || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Landlords
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2.5, borderRadius: 2, textAlign: "center" }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: "#f59e0b" }}>
                {stats?.tenant_count || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Tenants
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2.5, borderRadius: 2, textAlign: "center" }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: "#ef4444" }}>
                {allUsers.filter((u) => !u.is_active).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Inactive
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by name, email, or contact..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: "#9ca3af" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>User Type</InputLabel>
                <Select
                  value={filterType}
                  label="User Type"
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="landlord">Landlords</MenuItem>
                  <MenuItem value="tenant">Tenants</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterStatus}
                  label="Status"
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setSearchQuery("");
                  setFilterType("all");
                  setFilterStatus("all");
                }}
                sx={{ borderColor: "#e5e7eb" }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Data Table */}
        <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
          <DataGrid
            rows={filteredUsers}
            columns={columns}
            getRowId={(row) => row._id || Math.random()}
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
            }}
            pageSizeOptions={[10, 25, 50]}
            disableRowSelectionOnClick
            autoHeight
            sx={{
              border: "none",
              "& .MuiDataGrid-columnHeaders": {
                bgcolor: "#f8fafc",
                borderBottom: "1px solid #e5e7eb",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: 600,
                color: "#374151",
              },
              "& .MuiDataGrid-row:hover": {
                bgcolor: "#f8fafc",
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid #f3f4f6",
              },
            }}
          />
        </Paper>

        {/* User Details Dialog */}
        <Dialog
          open={detailsOpen}
          onClose={() => setDetailsOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              User Details
            </Typography>
            <IconButton onClick={() => setDetailsOpen(false)}>
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {selectedUser && (
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                  <Avatar
                    src={selectedUser.profileImage}
                    sx={{
                      width: 80,
                      height: 80,
                      bgcolor: selectedUser.userType === "landlord" ? "#22c55e20" : "#f59e0b20",
                      color: selectedUser.userType === "landlord" ? "#22c55e" : "#f59e0b",
                      fontSize: 32,
                    }}
                  >
                    {(selectedUser.name || "U").charAt(0).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {selectedUser.name || "N/A"}
                    </Typography>
                    <Chip
                      size="small"
                      label={selectedUser.userType === "landlord" ? "Landlord" : "Tenant"}
                      sx={{
                        bgcolor: selectedUser.userType === "landlord" ? "#22c55e15" : "#f59e0b15",
                        color: selectedUser.userType === "landlord" ? "#22c55e" : "#f59e0b",
                        fontWeight: 500,
                        mt: 0.5,
                      }}
                    />
                  </Box>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Email
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {selectedUser.email || "N/A"}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Contact
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {selectedUser.contact || "N/A"}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Status
                    </Typography>
                    <Box>
                      <Chip
                        size="small"
                        label={selectedUser.is_active ? "Active" : "Inactive"}
                        sx={{
                          bgcolor: selectedUser.is_active ? "#dcfce7" : "#fee2e2",
                          color: selectedUser.is_active ? "#166534" : "#991b1b",
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Registered
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {selectedUser.is_register ? "Yes" : "No"}
                    </Typography>
                  </Grid>
                  {selectedUser.address && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">
                        Address
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {selectedUser.address}
                      </Typography>
                    </Grid>
                  )}
                  {selectedUser.cnic && (
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        CNIC
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {selectedUser.cnic}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Joined Date
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {selectedUser.createdAt
                        ? new Date(selectedUser.createdAt).toLocaleDateString()
                        : "N/A"}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setDetailsOpen(false)} variant="outlined">
              Close
            </Button>
          </DialogActions>
        </Dialog>
        </>
        )}
      </Box>
    </>
  );
};

export default UserManagement;
